import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { isEmpty, sumBy, values as valuesFn } from 'lodash/fp';
import React, { FC, useRef } from 'react';

import { SortableDndItem } from '@portals/core';
import { ModalProps } from '@portals/framework';
import { ReactComponent as AddSquare } from '@portals/icons/linear/add-square.svg';
import { ReactComponent as Drag } from '@portals/icons/linear/drag.svg';
import { ReactComponent as Trash } from '@portals/icons/linear/trash.svg';
import {
  FieldTypeEnum,
  SupportedCommandFieldType,
  SupportedCommandType,
} from '@portals/types';
import { suppressPropagation } from '@portals/utils';

import { TYPES } from '../constants';

interface AddCustomFieldProps {
  onSubmit: (field: SupportedCommandFieldType, fieldName?: string) => void;
  custom_fields: SupportedCommandType['custom_fields'];
  field?: Partial<SupportedCommandFieldType>;
}

interface FormType {
  type: string;
  field: string;
  label: string;
  options: Array<{ value: string; label: string; order: number }>;
  isRequired: boolean;
  min: number;
  max: number;
}

const getInitialOptionsList = (field: Partial<SupportedCommandFieldType>) => {
  if (isEmpty(field.options)) {
    return [
      {
        value: '',
        label: '',
        order: 0,
      },
    ];
  }

  return valuesFn(field.options).sort((a, b) => a.order - b.order);
};

const CustomFieldForm: FC<ModalProps<AddCustomFieldProps>> = ({
  closeMe,
  data,
}) => {
  const { onSubmit, field = {}, custom_fields = [] } = data;
  const initialFieldName = useRef(field.name);
  const form = useForm<FormType>({
    initialValues: {
      type: field.type || '',
      field: field.name || '',
      label: (field.title as string) || '',
      options: getInitialOptionsList(field),
      isRequired: !!field.required,
      min: field.inputProps?.min || 0,
      max: field.inputProps?.max || 0,
    },

    clearInputErrorOnChange: true,

    validate: {
      field: (value) => {
        const isNewField = isEmpty(field);
        const nameAppearancesSum = sumBy(
          ({ name }) => (name === value ? 1 : 0),
          custom_fields
        );
        const isDuplicated =
          isNewField || initialFieldName.current !== value
            ? nameAppearancesSum > 0
            : nameAppearancesSum > 1;

        return isDuplicated
          ? `Field with "${value}" name already exists`
          : null;
      },

      options: {
        value: (fieldValue, allValues) =>
          sumBy(
            ({ value }) => (fieldValue === value ? 1 : 0),
            allValues.options
          ) > 1
            ? `Options values should be unique`
            : null,
      },
    },
  });

  const onSubmitWrapper = (values: FormType) => {
    if (!values.type) {
      form.setFieldError('type', 'Type is required');
      return;
    }

    const fieldType: SupportedCommandFieldType = {
      name: values.field,
      title: values.label || values.field,
      type: values.type as FieldTypeEnum,
      required: values.isRequired,
    };

    if (
      values.type === FieldTypeEnum.Select ||
      values.type === FieldTypeEnum.Multiselect
    ) {
      // convert options array into an object with `value`s as keys
      fieldType.options = values.options.reduce((acc, option, index) => {
        acc[option.value] = { ...option, order: index };

        return acc;
      }, {});
    } else if (values.type === FieldTypeEnum.Number) {
      fieldType.inputProps = {
        min: form.values.min,
        max: form.values.max,
      };
    } else if (values.type === FieldTypeEnum.Checkbox) {
      // Checkbox field cannot be required
      fieldType.required = false;
    }

    onSubmit(fieldType, initialFieldName.current);
    closeMe();
  };

  const onMove = (dragIndex: number, hoverIndex: number) => {
    form.reorderListItem('options', { from: dragIndex, to: hoverIndex });
  };

  return (
    <Modal title="Add custom field" opened onClose={closeMe}>
      <form onSubmit={suppressPropagation(form.onSubmit(onSubmitWrapper))}>
        <Stack>
          {form.values.type === FieldTypeEnum.Checkbox ? null : (
            <Checkbox
              label="Required"
              checked={form.values.isRequired}
              {...form.getInputProps('isRequired')}
            />
          )}

          <Group grow>
            <TextInput
              required
              data-testid="field-key-input"
              label="Field key"
              placeholder="is_active"
              {...form.getInputProps('field')}
            />

            <TextInput
              data-testid="label-input"
              label="Label"
              placeholder="Active"
              {...form.getInputProps('label')}
            />
          </Group>

          <Select
            required
            withinPortal
            data-testid="field-type-input"
            label="Field type"
            data={TYPES}
            {...form.getInputProps('type')}
          />

          {form.values.type === FieldTypeEnum.Number ? (
            <Group grow>
              <NumberInput
                label="Min"
                data-testid="minimum-value-input"
                {...form.getInputProps('min')}
              />

              <NumberInput
                label="Max"
                data-testid="maximum-value-input"
                {...form.getInputProps('max')}
              />
            </Group>
          ) : null}

          {form.values.type === FieldTypeEnum.Select ||
          form.values.type === FieldTypeEnum.Multiselect ? (
            <Stack spacing="md">
              <Divider variant="dashed" label="OPTIONS" />

              {form.values.options.map(({ value, label }, index) => (
                <SortableDndItem
                  key={index}
                  dndTypeIdentifier="option-field"
                  itemIndex={index}
                  itemUniqueId={index}
                  onMove={onMove}
                >
                  <Group align="center" p={4} sx={{ position: 'relative' }}>
                    <Box
                      c="gray.3"
                      sx={{
                        transform: 'rotate(-90deg) translateY(-40%)',
                        transformOrigin: 'right center',
                      }}
                    >
                      <Drag />
                    </Box>

                    <TextInput
                      required
                      data-testid="option-value-input"
                      label="Value"
                      sx={{ flex: '1 !important' }}
                      {...form.getInputProps(`options.${index}.value`)}
                    />

                    <TextInput
                      data-testid="option-label-input"
                      label="Label"
                      required
                      sx={{ flex: '1 !important' }}
                      {...form.getInputProps(`options.${index}.label`)}
                    />

                    <Box
                      pt="lg"
                      sx={
                        index === 0
                          ? { visibility: 'hidden', pointerEvents: 'none' }
                          : {}
                      }
                    >
                      <ActionIcon
                        onClick={() => form.removeListItem('options', index)}
                        sx={{
                          svg: { width: 16, height: 16 },
                        }}
                      >
                        <Trash />
                      </ActionIcon>
                    </Box>
                  </Group>
                </SortableDndItem>
              ))}

              <Group position="center" mt="md">
                <Button
                  variant="default"
                  color="blue_gray"
                  leftIcon={<AddSquare />}
                  onClick={() =>
                    form.insertListItem('options', {
                      value: '',
                      label: '',
                      order: form.values.options.length + 1,
                    })
                  }
                  sx={{
                    svg: {
                      height: 16,
                      width: 16,
                    },
                  }}
                >
                  Add option
                </Button>
              </Group>
            </Stack>
          ) : null}

          <Group position="right">
            <Button variant="default" onClick={closeMe}>
              Cancel
            </Button>

            <Button type="submit" data-testid="add-custom-field-submit-btn">
              {isEmpty(field) ? 'Add' : 'Update'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CustomFieldForm;
